import { LAST_CALL_SLEEVE_ID, LEAGUE_SLEEVE_IDS } from '@shared/index'

export const RANDOM_SLEEVE_ID = 'random'
export const DEFAULT_SLEEVE_ID = 'millennium-gaze'
/** Fallback house back if a catalog file is missing. */
export const CLASSIC_SLEEVE_URL = '/assets/cards/backs/Back6.webp'

export type SleeveDef = {
  id: string
  src: string
}

export const SLEEVES: SleeveDef[] = [
  { id: 'millennium-gaze', src: '/assets/sleeves/millennium-gaze.webp' },
  { id: 'heart-draw', src: '/assets/sleeves/heart-draw.webp' },
  { id: 'kingdom-isle', src: '/assets/sleeves/kingdom-isle.webp' },
  { id: 'city-skies', src: '/assets/sleeves/city-skies.webp' },
  { id: 'god-triad', src: '/assets/sleeves/god-triad.webp' },
  { id: 'ivory-wyrm', src: '/assets/sleeves/ivory-wyrm.webp' },
  { id: 'dusk-staff', src: '/assets/sleeves/dusk-staff.webp' },
  { id: 'carnival-ink', src: '/assets/sleeves/carnival-ink.webp' },
  { id: 'crimson-iris', src: '/assets/sleeves/crimson-iris.webp' },
  { id: 'five-relics', src: '/assets/sleeves/five-relics.webp' },
  { id: 'classic-vortex', src: '/assets/sleeves/classic-vortex.webp' },
  { id: 'sapphire-vortex', src: '/assets/sleeves/sapphire-vortex.webp' },
  { id: 'emerald-vortex', src: '/assets/sleeves/emerald-vortex.webp' },
  { id: 'amethyst-vortex', src: '/assets/sleeves/amethyst-vortex.webp' },
  { id: 'rose-vortex', src: '/assets/sleeves/rose-vortex.webp' },
  { id: 'silver-vortex', src: '/assets/sleeves/silver-vortex.webp' },
  { id: 'last-call', src: '/assets/sleeves/last-call.png' },
  ...LEAGUE_SLEEVE_IDS.map((id) => ({ id, src: `/assets/sleeves/${id}.webp` })),
]

/** Sleeves earned by reaching a ranked league. Listed in their own row. */
export const LEAGUE_SLEEVES = SLEEVES.filter((sleeve) =>
  LEAGUE_SLEEVE_IDS.includes(sleeve.id),
)
export const BASE_SLEEVES = SLEEVES.filter(
  (sleeve) => !LEAGUE_SLEEVE_IDS.includes(sleeve.id),
)

const BY_ID = new Map(SLEEVES.map((sleeve) => [sleeve.id, sleeve]))

export function isSleeveId(id: string): boolean {
  return BY_ID.has(id)
}

/**
 * Stored preference: a catalog id, or `random` for a new pick each duel.
 */
export function parseSleeveId(raw: unknown): string {
  if (raw === RANDOM_SLEEVE_ID) return RANDOM_SLEEVE_ID
  if (typeof raw === 'string' && BY_ID.has(raw)) return raw
  return DEFAULT_SLEEVE_ID
}

export function sleeveById(id: string): SleeveDef | undefined {
  return BY_ID.get(id)
}

/**
 * One catalog id at random. `excludeId` avoids matching the other seat.
 */
export function pickRandomSleeveId(excludeId?: string): string {
  const pool = SLEEVES.filter((sleeve) => {
    if (sleeve.id === LAST_CALL_SLEEVE_ID) return false
    if (LEAGUE_SLEEVE_IDS.includes(sleeve.id)) return false
    if (excludeId && sleeve.id === excludeId) return false
    return true
  })
  const index = Math.floor(Math.random() * pool.length)
  return pool[index]?.id ?? DEFAULT_SLEEVE_ID
}

/**
 * Texture used for this duel. Random is resolved once and stays.
 */
export function resolveDuelSleeveId(pref: string): string {
  const id = parseSleeveId(pref)
  if (id !== RANDOM_SLEEVE_ID) return id
  return pickRandomSleeveId()
}

export function sleeveTextureKey(id: string): string {
  return `sleeve-${id}`
}

export function sleeveUrl(id: string): string {
  return sleeveById(id)?.src ?? CLASSIC_SLEEVE_URL
}

/**
 * Art for menus. Random uses the default sleeve until a duel rolls.
 */
export function displaySleeveUrl(pref: string): string {
  const id = parseSleeveId(pref)
  if (id === RANDOM_SLEEVE_ID) return sleeveUrl(DEFAULT_SLEEVE_ID)
  return sleeveUrl(id)
}
